/* global React */
const { useState, useEffect, useRef } = React;

const CONTACT = {
  email: "Info@crmaps.com",
  officeHref: "tel:+50626435084",
  office: "(506) 2643-5084",
  whatsappPrimaryHref: "https://wa.me/50647073168",
  whatsappPrimary: "(506) 4707 3168",
  whatsappSecondaryHref: "https://wa.me/50626435084",
  whatsappSecondary: "(506) 2643 5084",
  facebook: "https://www.facebook.com/crmaps",
  instagram: "https://www.instagram.com/crmaps506",
  website: "https://www.crmaps.com",
  app: "https://www.crmaps.com/app-link.html"
};

const IMAGES = {
  hero: "/imagenes/ventas/hero-app.png",
  appMap: "/imagenes/ventas/multiple.webp",
  printed: "/imagenes/ventas/printed-maps.png",
  giganto: "/imagenes/ventas/giganto.jpg",
  jacoOne: "/imagenes/ventas/jaco-giant-1.jpg",
  jacoTwo: "/imagenes/ventas/jaco-giant-2.jpg",
  tamarindo: "/imagenes/ventas/tamarindo-giant.jpg",
  manuelAntonio: "/imagenes/ventas/manuel-antonio-giant.png"
};

const HERO_SLIDES = [
  { src: "/imagenes/ventas/1.webp", alt: "Traveler using the CRMaps app beside a resort pool in Costa Rica." },
  { src: "/imagenes/ventas/2.webp", alt: "Hotel staff handing a CRMaps printed map to a traveler." },
  { src: "/imagenes/ventas/3.webp", alt: "Travelers reading a large public CRMaps map display." },
  { src: "/imagenes/ventas/4.webp", alt: "Travelers using a CRMaps printed map near a waterfall." },
  { src: "/imagenes/ventas/5.webp", alt: "Visitors looking at a CRMaps public map near the beach." },
  { src: "/imagenes/ventas/6.webp", alt: "Travelers using the CRMaps app inside a car in Costa Rica." }
];

const FOOTER_COPY = {
  en: {
    brand: "Hand-painted, interactive Tourism Maps of Costa Rica. Built locally in San Jose, Costa Rica.",
    business: "Business",
    company: "Company",
    listPin: "For Business",
    sponsorRegion: "Sponsor a Region",
    talkSales: "Talk to sales",
    about: "About CRMaps",
    community: "Community",
    contact: "Contact",
    privacy: "Privacy",
    admin: "Admin",
    aboutModalTitle: "About CRMaps",
    aboutModalText: [
      "CRMaps is a Costa Rica tourism platform built around illustrated Regional Maps, local discovery and connected travel experiences. What started as a printed Map project evolved into a multi-platform ecosystem including Interactive Maps, Mobile Apps, tourism information and local business discovery tools designed to help travelers explore Costa Rica more visually and intuitively.",
      "We are committed to helping travelers discover authentic destinations while supporting local businesses, tourism communities and regional experiences throughout Costa Rica."
    ],
    privacyModalTitle: "Privacy Policy",
    privacyUpdated: "Last updated: May 2026",
    privacyModalText: [
      "Your privacy is important to us. We only ask for personal information when we truly need it to provide a service to you.",
      "We collect it by fair and lawful means, with your knowledge and consent. We only retain collected information for as long as necessary to provide your requested service.",
      "If you have any questions about how we handle user data and personal information, feel free to contact us."
    ],
    close: "Close",
    bottom: "© 2026 CRMaps · Made in Costa Rica · Pura vida"
  },
  es: {
    brand: "Mapas Turisticos de Costa Rica, ilustrados a mano e interactivos. Hecho localmente en San Jose, con carino.",
    business: "Negocios",
    company: "Compania",
    listPin: "Para negocios",
    sponsorRegion: "Patrocinar una region",
    talkSales: "Hablar con ventas",
    about: "Acerca de CRMaps",
    community: "Comunidad",
    contact: "Contacto",
    privacy: "Privacidad",
    admin: "Admin",
    aboutModalTitle: "Acerca de CRMaps",
    aboutModalText: [
      "CRMaps es una plataforma turistica de Costa Rica construida alrededor de Mapas Regionales ilustrados, descubrimiento local y experiencias de viaje conectadas.",
      "Apoyamos a negocios locales, comunidades turisticas y experiencias regionales en toda Costa Rica para que los viajeros descubran el pais de forma mas visual e intuitiva."
    ],
    privacyModalTitle: "Politica de privacidad",
    privacyUpdated: "Ultima actualizacion: mayo de 2026",
    privacyModalText: [
      "Tu privacidad es importante para nosotros. Solo solicitamos informacion personal cuando realmente la necesitamos para brindarte un servicio.",
      "La recopilamos por medios justos y legales, con tu conocimiento y consentimiento. Conservamos la informacion solo durante el tiempo necesario.",
      "Si tenes preguntas sobre como manejamos los datos de usuarios e informacion personal, podes contactarnos."
    ],
    close: "Cerrar",
    bottom: "© 2026 CRMaps · Hecho en Costa Rica · Pura vida"
  }
};

const I18N = {
  en: {
    meta: {
      title: "Advertise with CRMaps | Tourism Advertising in Costa Rica",
      description: "Promote your business with CRMaps through printed maps, mobile app, interactive digital maps, website visibility, social media reach and strategic tourism distribution in Costa Rica."
    },
    nav: {
      home: "Home",
      guide: "Costa Rica Guide",
      cta: "List your business",
      openMenu: "Open menu",
      tideWeather: "Tide Chart & Weather",
      gastronomy: "Gastronomy",
      history: "History",
      geography: "Geography",
      items: [
        { id: "top", label: "Home" },
        { id: "included", label: "Included" },
        { id: "pricing", label: "Options" },
        { id: "regions", label: "Regions" },
        { id: "contact", label: "Contact" }
      ]
    },
    hero: {
      kicker: "Advertise with CRMaps",
      title: "Put your business in front of",
      em: "thousands of travelers with CRMaps",
      sub: "Printed maps, interactive digital maps, mobile app, web directory and strong social media exposure, all connected so travelers can discover and contact your business. We can help you sell MORE!!! and provide excellent attention to your customers.",
      intro: "We're preparing the next edition of CRMaps and we'd love to include your business. We can help promote you across our printed maps, app, website and social media.",
      primary: "Contact CRMaps",
      secondary: "Advertising options",
      badge: "Live on iOS & Android",
      edition: "Next edition",
      editionSub: "Open for advertisers",
      alt: "A traveler at a Costa Rican resort exploring the CRMaps app on her phone"
    },
    ecosystem: {
      kicker: "The CRMaps ecosystem",
      title: "Four channels.",
      em: "One placement.",
      lede: "Every advertiser gets exposure across the full CRMaps tourism network - connected so travelers can discover, contact and find your business however they're planning their trip.",
      cards: [
        { n: "01", tone: "", title: "Mobile App", text: "iOS and Android app with interactive digital maps. Tapping a hotspot gives travelers one-click access to everything they need to find and contact your business: Waze, Google Maps, WhatsApp, phone, website, social media and direct links.", metric: "+50,000", metricLabel: "installations", icon: "phone" },
        { n: "02", tone: "eco-card--orange", title: "Website", text: "Interactive regional maps with direct access to Waze, Google Maps, WhatsApp, phone, web and social media, plus a full Costa Rica directory so travelers can find and contact you easily. We've also refreshed the site with useful content for tourists — history, geography, weather, gastronomy and more — helping them discover Costa Rica before and during their trip.", metric: "+19,000", metricLabel: "Weekly requests", icon: "globe" },
        { n: "03", tone: "eco-card--green", title: "Social Media", text: "Facebook and Instagram with strong tourism reach and a community of over 63,000 followers. We continuously repost our advertisers' updates to extend their exposure and keep their businesses visible to travelers interested in Costa Rica.", metric: "+350K", metricLabel: "monthly reach", icon: "clock" },
        { n: "04", tone: "eco-card--yellow", title: "Printed Presence", text: "Printed maps, posters and giant maps in high-traffic areas. All our advertisers are map distribution points, and we also distribute our maps at strategic locations throughout the country.", metric: "Bimonthly", metricLabel: "6 editions per year", icon: "map" }
      ]
    },
    included: {
      kicker: "What's included",
      title: "One placement.",
      em: "Multiple exposure channels.",
      lede: "Every advertising option includes presence across CRMaps printed maps, mobile app, website and social media support - designed to keep your business visible from the moment a traveler starts planning.",
      overlayTitle: "One ad, every surface",
      overlayText: "Printed map · Giant map · Posters · App · Web · Social media",
      alt: "Hand holding a phone displaying CRMaps next to the hand-illustrated Costa Rica map showing every available region.",
      items: ["Printed map advertising", "Interactive digital map presence", "App and web directory listing", "Waze and Google Maps integration", "WhatsApp integration", "Website links & advertiser info", "Continuous social media repost", "Artwork prepared at no extra charge"]
    },
    printed: {
      kicker: "Print, posters & huge public maps",
      title: "Printed maps that travelers",
      em: "actually use",
      lede: "CRMaps printed editions are distributed in strategic tourism areas and designed to help visitors discover places to eat, stay, shop, explore and book activities.",
      tiles: [
        { src: "/imagenes/ventas/10.webp", alt: "CRMaps giant public map displayed at JacoWalk Open Air Shopping Center at night." },
        { src: "/imagenes/ventas/9.webp", alt: "Hotel staff handing a CRMaps printed map to a traveler at the front desk." },
        { src: "/imagenes/ventas/8.webp", alt: "Couple reading the CRMaps giant map display at an outdoor tourism area." },
        { src: "/imagenes/ventas/7.webp", alt: "Illuminated CRMaps giant map table in Tamarindo at night." },
        { src: "/imagenes/ventas/3.webp", alt: "Travelers reading the CRMaps map at a public kiosk in La Fortuna." },
        { src: "/imagenes/ventas/2.webp", alt: "Hotel receptionist handing a CRMaps printed map to a tourist at check-in." }
      ],
      points: [
        ["Bimonthly editions", "Fresh every 2 months"],
        ["Maps + posters", "Plus huge public maps"],
        ["Strategic spots", "High-traffic tourism areas"],
        ["Travel-friendly", "Designed for visitors"],
        ["Strong visibility", "Full edition cycle"]
      ]
    },
    pricing: {
      kicker: "Advertising options",
      title: "Pick a size.",
      em: "Reach every channel.",
      lede: "Three printed-map ad sizes - every one carries through to the app, website and social channels.",
      cards: [
        { cls: "price-card--six", sampleImg: "/imagenes/ventas/1-6.webp", sample: "1/6 page", name: "1/6 Page", tag: "Compact & effective", price: "₡95,000", cycle: "Per 2-month edition", cta: "Reserve 1/6 placement", items: ["Social media repost", "Custom illustration in printed map", "Printed ad on the back of the map", "App and web directory listing", "Interactive information", "Free artwork preparation"] },
        { cls: "price-card--third price-card--featured", badge: "Most popular", sampleImg: "/imagenes/ventas/2-6.webp", sample: "1/3 page", name: "1/3 Page", tag: "Best balance for advertisers", price: "₡175,000", cycle: "Per 2-month edition", cta: "Reserve 1/3 placement", items: ["Larger printed presence", "Social media repost", "Custom illustration in printed map", "Printed ad on the back of the map", "App and web directory listing", "Interactive information", "Free artwork preparation"] },
        { cls: "price-card--two-thirds", sampleImg: "/imagenes/ventas/4-6.webp", sample: "2/3 page", name: "2/3 Page", tag: "Maximum visibility", price: "₡325,000", cycle: "Per 2-month edition", cta: "Reserve 2/3 placement", items: ["Dominant printed placement", "Priority social media repost", "Custom illustration in printed map", "Printed ad on the back of the map", "App and web directory listing", "Interactive information", "Free artwork preparation"] }
      ],
      tax: "+ IVA",
      noteTitle: "Artwork on us.",
      noteText: "CRMaps can prepare the artwork for your ad at no extra charge for any of the options above.",
      contactBtn: "Contact us",
      prefillTemplate: "I'm interested in the {option} advertising option.",
    },
    regions: {
      kicker: "Available regions",
      title: "Seven regional maps across",
      em: "Costa Rica",
      lede: "Pick the regions that match your visitors - your ad appears across every CRMaps surface for that region.",
      items: [
        ["Jaco", "Central Pacific", "/imagenes/ventas/LOGO PJ.webp"],
        ["Manuel Antonio & Quepos", "Central Pacific", "/imagenes/ventas/LOGO MA.webp"],
        ["Arenal Volcano & La Fortuna", "Northern lowlands", "/imagenes/ventas/LOGO FA.webp"],
        ["Tamarindo", "Guanacaste", "/imagenes/ventas/LOGO TA.webp"],
        ["Herradura", "Central Pacific", "/imagenes/ventas/LOGO HE.webp"],
        ["Playas del Coco & Hermosa", "Guanacaste", "/imagenes/ventas/LOGO CH.webp"],
        ["Costa Ballena", "Dominical, Uvita & Ojochal", "/imagenes/ventas/LOGO DU.webp"]
      ]
    },
    why: {
      kicker: "Why CRMaps",
      title: "Why businesses choose to",
      em: "advertise with us",
      lede: "CRMaps connects travelers before, during and after their trip - across the surfaces they actually use to find places like yours.",
      items: [
        "Travelers can find your business across printed maps, interactive digital maps, mobile app and web directory.",
        "Each hotspot gives travelers one-click access to WhatsApp, Waze, Google Maps, phone, website, social media and direct advertiser links.",
        "Our maps are not just flyers — their visual quality, character and illustrated style make many tourists keep them as a travel souvenir.",
        "Printed maps keep circulating after the vacation: tourists keep them, show them and share them when they get home.",
        "CRMaps maintains an active community of over 63,000 followers on Facebook and Instagram with strong tourism reach.",
        "We continuously repost our advertisers' updates to extend their exposure and keep them visible to travelers interested in Costa Rica.",
        "Your business appears in real tourism discovery tools, not generic listings.",
        "The printed edition stays visible throughout the full 2-month cycle, reinforced by digital presence on web, app and social media."
      ]
    },
    contact: {
      kicker: "Let's talk",
      title: "Let's prepare your ad for the",
      em: "next edition",
      lede: "Tell us about your business and the Region you serve — we'll get back within one business day with the right plan.",
      whatsapp: "WhatsApp · +506 4707 3168",
      whatsappSub: "Fastest response, business hours",
      call: "Call · +506 2643 5084",
      callSub: "Mon–Fri · 8am–5pm CST",
      email: "info@crmaps.com",
      emailSub: "Email — replies within 24h",
      success: "✓ Thanks — we'll be in touch within one business day.",
      error: "We couldn't send your inquiry. Please try again or email us directly.",
      sending: "Sending...",
      labels: {
        name: "Your name",
        business: "Business name",
        email: "Email",
        whatsapp: "WhatsApp",
        type: "Business type",
        region: "Region",
        message: "Tell us about your business",
      },
      businessTypes: ["Accommodation", "Restaurant", "Tour operator", "Shop", "Transport", "Other"],
      regions: ["Jaco Beach", "Manuel Antonio & Quepos", "Tamarindo", "Arenal Volcano & La Fortuna", "Herradura", "Playas del Coco & Hermosa", "Costa Ballena", "Other"],
      messagePlaceholder: "What you do, where you are, and what you're hoping CRMaps can help with...",
      disclaimer: "By submitting you agree to be contacted about CRMaps listings. We never share your details.",
      submit: "Send inquiry",
    },
    footer: FOOTER_COPY.en
  },
  es: {
    meta: {
      title: "Anunciate con CRMaps | Publicidad turistica en Costa Rica",
      description: "Promove tu negocio con CRMaps por medio de mapas impresos, app movil, mapas digitales interactivos, visibilidad web, redes sociales y distribucion turistica estrategica en Costa Rica."
    },
    nav: {
      home: "Inicio",
      guide: "Guia de Costa Rica",
      cta: "Anuncia tu negocio",
      openMenu: "Abrir menu",
      tideWeather: "Mareas y Clima",
      gastronomy: "Gastronomia",
      history: "Historia",
      geography: "Geografia",
      items: [
        { id: "top", label: "Inicio" },
        { id: "included", label: "Incluye" },
        { id: "pricing", label: "Opciones" },
        { id: "regions", label: "Regiones" },
        { id: "contact", label: "Contacto" }
      ]
    },
    hero: {
      kicker: "Anunciá con CRMaps",
      title: "Poné tu negocio frente a",
      em: "miles de viajeros con CRMaps",
      sub: "Mapas impresos, mapas digitales interactivos, app móvil, directorio web y una fuerte exposición en redes sociales: todo conectado para que los viajeros descubran, conozcan y contacten su negocio. CRMaps puede ayudarle a vender más y a brindar una mejor atención a sus clientes.",
      intro: "Estamos preparando la próxima edición de CRMaps y nos encantaría contar con su negocio. Podemos ayudarle a promocionarse en nuestros mapas impresos, app, web, directorio y redes sociales.",
      primary: "Contactar CRMaps",
      secondary: "Opciones de anuncio",
      badge: "App disponible en iOS y Android",
      edition: "Próxima edición.",
      editionSub: "Abierta para anunciantes",
      alt: "Viajera en un resort de Costa Rica explorando la app de CRMaps en su telefono"
    },
    ecosystem: {
      kicker: "El ecosistema CRMaps",
      title: "Cuatro canales.",
      em: "Una ubicacion.",
      lede: "Cada anunciante recibe exposicion en la red turistica completa de CRMaps, conectada para que los viajeros descubran, contacten y encuentren tu negocio mientras planean o disfrutan su viaje.",
      cards: [
        { n: "01", tone: "", title: "App movil", text: "App para iOS y Android con mapas digitales interactivos. Al tocar un hotspot, el viajero accede en un solo click a toda la información del anunciante: Waze, Google Maps, WhatsApp, teléfono, sitio web, redes sociales y enlaces directos para contactar o llegar a su negocio..", metric: "+50,000", metricLabel: "instalaciones historicas", icon: "phone" },
        { n: "02", tone: "eco-card--orange", title: "Sitio web", text: "Mapas regionales interactivos con acceso directo a Waze, Google Maps, WhatsApp, teléfono, web y redes sociales, además de un directorio completo de Costa Rica para que los viajeros lo encuentren y contacten fácilmente. También renovamos la web con información útil para el turista como historia, geografía, clima, gastronomía y más, ayudándo a conocer mejor Costa Rica antes y durante su viaje.", metric: "+19,000", metricLabel: "Solicitudes semanales", icon: "globe" },
        { n: "03", tone: "eco-card--green", title: "Redes sociales", text: "Facebook e Instagram con fuerte alcance turístico y una comunidad de más de 63.000 seguidores. Realizamos un reposteo continuo de las novedades de nuestros anunciantes para ampliar su exposición y mantener sus negocios visibles ante viajeros interesados en Costa Rica.", metric: "+350K", metricLabel: "alcance mensual", icon: "clock" },
        { n: "04", tone: "eco-card--yellow", title: "Presencia impresa", text: "Mapas impresos, posters y mapas gigantes en zonas de alto transito. Todos nuestros anunciantes son puntos de distribucion del Mapa, ademas distribuimos nuestros mapas en puntos estratégicos a lo largo de todo el pais.", metric: "Bimestral", metricLabel: "6 ediciones anuales", icon: "map" }
      ]
    },
    included: {
      kicker: "Que incluye",
      title: "Una ubicacion.",
      em: "Multiples canales de exposición.",
      lede: "Cada opción publicitaria incluye presencia en mapas impresos, app movil, sitio web y reposteo continuo en redes sociales para mantener visible tu negocio desde que el viajero empieza a planear.",
      overlayTitle: "Un anuncio, todos los medios",
      overlayText: "Mapa impreso · Mapa Gigante · Posters · App · Web · Redes sociales",
      alt: "Mimagen multiplataforma de CRMaps.",
      items: ["Publicidad en mapa impreso", "Presencia en mapa digital interactivo", "Directorio en app movil y web", "Integracion con Waze y Google Maps", "Integracion con WhatsApp", "Links web e informacion del anunciante", "Reposteo continuo en redes sociales", "Arte preparado sin costo adicional"]
    },
    printed: {
      kicker: "Mapas impresos, afiches y mapas gigantes",
      title: "Mapas impresos que los viajeros",
      em: "si usan",
      lede: "Las ediciones impresas de CRMaps se distribuyen en zonas turisticas estrategicas y estan disenadas para ayudar a los visitantes a descubrir donde comer, hospedarse, comprar, explorar, contratar servicios y reservar.",
      tiles: [
        { src: "/imagenes/ventas/10.webp", alt: "Mapa gigante publico de CRMaps en JacoWalk Open Air Shopping Center de noche." },
        { src: "/imagenes/ventas/9.webp", alt: "Personal de hotel entregando un mapa impreso de CRMaps a un viajero en recepcion." },
        { src: "/imagenes/ventas/8.webp", alt: "Pareja leyendo el mapa gigante de CRMaps en una zona turistica al aire libre." },
        { src: "/imagenes/ventas/7.webp", alt: "Mesa con mapa gigante iluminado de CRMaps en Tamarindo de noche." },
        { src: "/imagenes/ventas/3.webp", alt: "Viajeros leyendo el mapa de CRMaps en un kiosko publico en La Fortuna." },
        { src: "/imagenes/ventas/2.webp", alt: "Recepcionista entregando un mapa impreso de CRMaps a una turista al hacer check-in." }
      ],
      points: [
        ["Ediciones bimestrales", "Cada 2 meses"],
        ["Mapas + posters", "Mas mapas gigantes"],
        ["Puntos estrategicos", "Zonas turisticas de alto trafico"],
        ["Hecho para viajeros", "Disenado para visitantes"],
        ["Alta visibilidad", "Durante todo el ciclo"]
      ]
    },
    pricing: {
      kicker: "Opciones publicitarias",
      title: "Elegi el tamaño.",
      em: "Incluye todos los canales.",
      lede: "Tres tamanos de anuncio en mapa impreso, y todos se extienden a la app, el sitio web y redes sociales.",
      cards: [
        { cls: "price-card--six", sampleImg: "/imagenes/ventas/1-6.webp", sample: "1/6 pagina", name: "1/6 Pagina", tag: "Compacto y efectivo", price: "₡95,000", cycle: "Por edicion de 2 meses", cta: "Reservar 1/6 pagina", items: ["Reposteo en redes sociales", "Dibujo personalizado en mapa impreso", "Anuncio impreso en la parte posterior del mapa", "Listado en directorio app y web", "Informacion interactiva", "Preparacion de arte gratis"] },
        { cls: "price-card--third price-card--featured", badge: "Recomendado", sampleImg: "/imagenes/ventas/2-6.webp", sample: "1/3 pagina", name: "1/3 Pagina", tag: "Mejor balance para anunciantes", price: "₡175,000", cycle: "Por edicion de 2 meses", cta: "Reservar 1/3 pagina", items: ["Mayor presencia impresa", "Reposteo en redes sociales", "Dibujo personalizado en mapa impreso", "Anuncio impreso en la parte posterior del mapa", "Listado en directorio app y web", "Informacion interactiva", "Preparacion de arte gratis"] },
        { cls: "price-card--two-thirds", sampleImg: "/imagenes/ventas/4-6.webp", sample: "2/3 pagina", name: "2/3 Pagina", tag: "Maxima visibilidad", price: "₡325,000", cycle: "Por edicion de 2 meses", cta: "Reservar 2/3 pagina", items: ["Ubicacion impresa dominante", "Repost prioritario en redes", "Dibujo personalizado en mapa impreso", "Anuncio impreso en la parte posterior del mapa", "Listado en directorio app y web", "Informacion interactiva", "Preparacion de arte gratis"] }
      ],
      tax: "+ IVA",
      noteTitle: "El arte va por cuenta nuestra.",
      noteText: "CRMaps puede preparar el arte de tu anuncio sin costo adicional para cualquiera de las opciones anteriores.",
      contactBtn: "Contáctenos",
      prefillTemplate: "Me interesa la opción {option}.",
    },
    regions: {
      kicker: "Regiones disponibles",
      title: "Siete Mapas regionales en",
      em: "Costa Rica",
      lede: "Elegí la/s región/es de tu interés. Tu anuncio aparece en todos los canales de CRMaps de esa región.",
      items: [
        ["Jaco", "Pacifico Central", "/imagenes/ventas/LOGO PJ.webp"],
        ["Manuel Antonio y Quepos", "Pacifico Central", "/imagenes/ventas/LOGO MA.webp"],
        ["Volcan Arenal y La Fortuna", "Llanuras del norte", "/imagenes/ventas/LOGO FA.webp"],
        ["Tamarindo", "Guanacaste", "/imagenes/ventas/LOGO TA.webp"],
        ["Herradura", "Pacifico Central", "/imagenes/ventas/LOGO HE.webp"],
        ["Playas del Coco y Hermosa", "Guanacaste", "/imagenes/ventas/LOGO CH.webp"],
        ["Costa Ballena", "Dominical, Uvita y Ojochal", "/imagenes/ventas/LOGO DU.webp"]
      ]
    },
    why: {
      kicker: "Por que CRMaps",
      title: "Por que los negocios eligen",
      em: "anunciarse con nosotros",
      lede: "CRMaps conecta viajeros antes, durante y despues de su viaje, en los medios que realmente usan para encontrar lugares como el tuyo.",
      items: [
         "Los viajeros pueden encontrar tu negocio en mapas impresos, mapas digitales interactivos, app móvil y directorio web.",
    "Cada hotspot permite acceder en un solo click a WhatsApp, Waze, Google Maps, teléfono, web, redes sociales y links directos del anunciante.",
    "Nuestros mapas no son simples folletos: por su calidad visual, simpatía y estilo ilustrado, muchos turistas los conservan como recuerdo de viaje.",
    "El mapa impreso sigue circulando después de las vacaciones: los turistas lo guardan, lo muestran y lo comparten al regresar a casa.",
    "CRMaps mantiene una comunidad activa de más de 63.000 seguidores en Facebook e Instagram, con fuerte alcance turístico.",
    "Realizamos un reposteo continuo de las novedades de nuestros anunciantes para ampliar su exposición y mantenerlos visibles ante viajeros interesados en Costa Rica.",
    "Tu negocio aparece en herramientas reales de descubrimiento turístico, no en listados genéricos.",
    "La edición impresa se mantiene visible durante todo el ciclo de 2 meses, reforzada por presencia digital en web, app y redes sociales."
      ]
    },
    contact: {
      kicker: "Hablemos",
      title: "Preparemos tu anuncio para la",
      em: "próxima edición",
      lede: "Contanos sobre tu negocio y la región donde operás. Te respondemos en un día hábil con el plan adecuado.",
      whatsapp: "WhatsApp · +506 4707 3168",
      whatsappSub: "Respuesta más rápida, horario laboral",
      call: "Llamar · +506 2643 5084",
      callSub: "Lun–Vie · 8am–5pm CST",
      email: "info@crmaps.com",
      emailSub: "Email — respondemos en menos de 24h",
      success: "✓ Gracias; te contactaremos en un día hábil.",
      error: "No pudimos enviar tu consulta. Intentá de nuevo o escribinos directamente.",
      sending: "Enviando...",
      labels: {
        name: "Tu nombre",
        business: "Nombre del negocio",
        email: "Email",
        whatsapp: "WhatsApp",
        type: "Tipo de negocio",
        region: "Región",
        message: "Contanos sobre tu negocio",
      },
      businessTypes: ["Hospedaje", "Restaurante", "Tour operador", "Tienda", "Transporte", "Otro"],
      regions: ["Jaco Beach", "Manuel Antonio & Quepos", "Tamarindo", "Arenal Volcano & La Fortuna", "Herradura", "Playas del Coco & Hermosa", "Costa Ballena", "Otra"],
      messagePlaceholder: "Qué hacés, dónde estás y qué esperás que CRMaps pueda ayudar a lograr...",
      disclaimer: "Al enviar aceptás que te contactemos sobre listados de CRMaps. Nunca compartimos tus datos.",
      submit: "Enviar consulta",
    },
    footer: FOOTER_COPY.es
  }
};

window.CRMAPS_I18N = I18N;

function LanguageToggle({ lang, onChange }) {
  return (
    <div className="lang-toggle" aria-label="Language selector">
      <button type="button" className={lang === "es" ? "is-on" : ""} onClick={() => onChange("es")} aria-pressed={lang === "es"}>ES</button>
      <button type="button" className={lang === "en" ? "is-on" : ""} onClick={() => onChange("en")} aria-pressed={lang === "en"}>EN</button>
    </div>
  );
}

function Icon({ type }) {
  const props = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round", strokeLinejoin: "round" };
  if (type === "phone") return <svg {...props}><rect x="6" y="2" width="12" height="20" rx="2.5" /><line x1="11" y1="18" x2="13" y2="18" /></svg>;
  if (type === "globe") return <svg {...props}><circle cx="12" cy="12" r="9" /><path d="M3 12h18" /><path d="M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></svg>;
  if (type === "clock") return <svg {...props}><path d="M21 11.5a8.5 8.5 0 1 1-3.5-6.9L21 4v4h-4" /><path d="M12 7v5l3 2" /></svg>;
  if (type === "pin") return <svg {...props} strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>;
  return <svg {...props}><polygon points="3 7 9 4 15 7 21 4 21 17 15 20 9 17 3 20 3 7" /><line x1="9" y1="4" x2="9" y2="17" /><line x1="15" y1="7" x2="15" y2="20" /></svg>;
}

function SalesNav({ activeSection, lang, onLanguageChange, copy }) {
  const [tucked, setTucked] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const guideRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setTucked(window.scrollY > 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!guideOpen) return undefined;
    const handler = (event) => {
      if (guideRef.current && !guideRef.current.contains(event.target)) setGuideOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [guideOpen]);

  useEffect(() => {
    if (!menuOpen && !guideOpen) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setGuideOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen, guideOpen]);

  return (
    <React.Fragment>
      <div className="site-lang-toggle">
        <LanguageToggle lang={lang} onChange={onLanguageChange} />
      </div>
      <nav className={`float-nav ${tucked ? "is-tucked" : ""}`} aria-label="CRMaps navigation">
        <a href="/#top" className="float-nav__brand" aria-label="CRMaps home">
          <img src="/imagenes/index/logo_menu.png" alt="CRMaps" />
        </a>
        <div className="float-nav__items">
          <div className="float-nav__page-links">
            {copy.nav.items.map((item) => (
              <a key={item.id} href={`#${item.id}`} className={activeSection === item.id ? "is-active" : ""}>{item.label}</a>
            ))}
          </div>
          <div className="cr-guide-wrapper" ref={guideRef}>
            <button type="button" className="cr-guide-btn" aria-expanded={guideOpen} onClick={() => setGuideOpen((value) => !value)}>
              {copy.nav.guide}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {guideOpen && (
              <div className="cr-guide-dropdown">
                <a href="/en/tide-chart/" onClick={() => setGuideOpen(false)}>{copy.nav.tideWeather}</a>
                <a href="/en/gastronomy/" onClick={() => setGuideOpen(false)}>{copy.nav.gastronomy}</a>
                <a href="/en/history/" onClick={() => setGuideOpen(false)}>{copy.nav.history}</a>
                <a href="/en/geography/" onClick={() => setGuideOpen(false)}>{copy.nav.geography}</a>
              </div>
            )}
          </div>
          <a href="#contact" className="float-nav__cta">{copy.nav.cta}</a>
        </div>
      </nav>
      <button type="button" className="hamburger" aria-label={copy.nav.openMenu} aria-expanded={menuOpen} aria-controls="hamburgerPanel" onClick={() => setMenuOpen((value) => !value)}>
        <span aria-hidden="true" />
      </button>
      <div id="hamburgerPanel" className={`hamburger-panel ${menuOpen ? "is-open" : ""}`}>
        {copy.nav.items.map((item) => (
          <a key={item.id} href={`#${item.id}`} onClick={() => setMenuOpen(false)}>{item.label}</a>
        ))}
      </div>
    </React.Fragment>
  );
}

function SalesHero({ copy }) {
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % HERO_SLIDES.length);
    }, 3000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <section className="hero" id="top" data-screen-label="01 Hero">
      <div className="hero__grid">
        <div className="hero__copy">
          <div className="hero__pill">{copy.hero.kicker}</div>
          <h1 className="hero__title">{copy.hero.title} <em>{copy.hero.em}</em></h1>
          <p className="hero__sub">{copy.hero.sub}</p>
          <p className="hero__intro">{copy.hero.intro}</p>
          <div className="hero__cta">
            <a href="#contact" className="btn btn--primary">{copy.hero.primary}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="16" height="16"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg></a>
            <a href="#pricing" className="btn btn--ghost">{copy.hero.secondary}</a>
          </div>
        </div>
        <div className="hero__media-wrap">
          <div className="hero__media">
            {HERO_SLIDES.map((slide, index) => (
              <img
                key={slide.src}
                className={`hero__slide ${index === activeSlide ? "is-active" : ""}`}
                src={slide.src}
                alt={index === activeSlide ? slide.alt : ""}
                aria-hidden={index === activeSlide ? "false" : "true"}
                loading={index === 0 ? "eager" : "lazy"}
              />
            ))}
            <div className="hero__media-badge">{copy.hero.badge}</div>
            <div className="hero__edition">
              <div className="hero__edition-pulse">2026</div>
              <div><strong>{copy.hero.edition}</strong><span>{copy.hero.editionSub}</span></div>
            </div>
          </div>
          <div className="hero__dots" role="tablist" aria-label="Hero slideshow">
            {HERO_SLIDES.map((slide, index) => (
              <button
                key={slide.src}
                type="button"
                className={index === activeSlide ? "is-active" : ""}
                aria-label={`Show slide ${index + 1}`}
                aria-selected={index === activeSlide}
                onClick={() => setActiveSlide(index)}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="hero__ecosystem" id="ecosystem" data-screen-label="02 Ecosystem" aria-label={copy.nav.items[0].label}>
        <EcosystemCards copy={copy} />
      </div>
    </section>
  );
}

function EcosystemCards({ copy }) {
  return (
    <div className="eco-grid">
      {copy.ecosystem.cards.map((card) => (
        <article className={`eco-card ${card.tone}`} key={card.n}>
          <div className="eco-card__num">{card.n}</div>
          <div className="eco-card__icon" aria-hidden="true"><Icon type={card.icon} /></div>
          <h3>{card.title}</h3>
          <p>{card.text}</p>
          <div className="eco-card__metric"><strong>{card.metric}</strong><span>{card.metricLabel}</span></div>
        </article>
      ))}
    </div>
  );
}

function Ecosystem({ copy }) {
  return (
    <section className="section section--cream" data-screen-label="02 Ecosystem">
      <div className="section-inner">
        <div className="eco-head">
          <div><div className="kicker">{copy.ecosystem.kicker}</div><h2 className="title">{copy.ecosystem.title} <em>{copy.ecosystem.em}</em></h2></div>
          <p className="lede">{copy.ecosystem.lede}</p>
        </div>
        <EcosystemCards copy={copy} />
      </div>
    </section>
  );
}

function Included({ copy }) {
  return (
    <section className="section" id="included" data-screen-label="03 What's included">
      <div className="section-inner">
        <div className="incl-grid">
          <div className="incl-left">
            <div className="incl-visual">
              <img src={IMAGES.appMap} alt={copy.included.alt} loading="lazy" />
            </div>
            <div className="incl-caption">
              <strong>{copy.included.overlayTitle}</strong>
              <span>{copy.included.overlayText}</span>
            </div>
          </div>
          <div>
            <div className="kicker">{copy.included.kicker}</div>
            <h2 className="title">{copy.included.title} <em>{copy.included.em}</em></h2>
            <p className="lede">{copy.included.lede}</p>
            <ul className="incl-list" aria-label={copy.included.kicker}>
              {copy.included.items.map((item, index) => <li className={`incl-item ${index === copy.included.items.length - 1 ? "is-star" : ""}`} key={item}>{item}</li>)}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function PrintedPresence({ copy }) {
  return (
    <section className="section section--cream" id="printed" data-screen-label="04 Printed presence">
      <div className="section-inner">
        <div className="print-head">
          <div><div className="kicker">{copy.printed.kicker}</div><h2 className="title">{copy.printed.title} <em>{copy.printed.em}</em></h2></div>
          <p className="lede">{copy.printed.lede}</p>
        </div>
        <div className="print-collage">
          {copy.printed.tiles.map((tile) => <figure className="print-tile" key={tile.src}><img src={tile.src} alt={tile.alt} loading="lazy" /></figure>)}
        </div>
        <div className="print-points">
          {copy.printed.points.map(([title, text]) => <div className="print-point" key={title}><strong>{title}</strong><span>{text}</span></div>)}
        </div>
      </div>
    </section>
  );
}

function Pricing({ copy }) {
  const handleContactClick = (cardName) => {
    const msg = copy.pricing.prefillTemplate.replace('{option}', cardName);
    window.dispatchEvent(new CustomEvent('crmaps-contact-prefill', { detail: { message: msg } }));
  };
  return (
    <section className="section" id="pricing" data-screen-label="05 Advertising options">
      <div className="section-inner">
        <div className="section-heading-center">
          <div className="kicker">{copy.pricing.kicker}</div>
          <h2 className="title">{copy.pricing.title} <em>{copy.pricing.em}</em></h2>
          <p className="lede">{copy.pricing.lede}</p>
        </div>
        <div className="pricing-grid">
          {copy.pricing.cards.map((card) => (
            <article className={`price-card ${card.cls}`} key={card.name}>
              {card.badge && <div className="price-card__badge">{card.badge}</div>}
              <div className="price-card__sample"><img src={card.sampleImg} alt={card.sample} loading="lazy" /></div>
              <div className="price-card__name">{card.name}</div>
              <span className="price-card__tag">{card.tag}</span>
              <ul>{card.items.map((item) => <li key={item}>{item}</li>)}</ul>
              <a href="#contact" className="price-card__cta" onClick={() => handleContactClick(card.name)}>{copy.pricing.contactBtn}</a>
            </article>
          ))}
        </div>
        <div className="pricing-note">
          <div className="pricing-note__icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19l7-7 3 3-7 7-3-3z" /><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" /><line x1="2" y1="2" x2="9.586" y2="9.586" /></svg></div>
          <p><strong>{copy.pricing.noteTitle}</strong> {copy.pricing.noteText}</p>
        </div>
      </div>
    </section>
  );
}

function Regions({ copy }) {
  return (
    <section className="section section--cream" id="regions" data-screen-label="06 Regions">
      <div className="section-inner">
        <div className="section-heading-left">
          <div className="kicker">{copy.regions.kicker}</div>
          <h2 className="title">{copy.regions.title} <em>{copy.regions.em}</em></h2>
          <p className="lede">{copy.regions.lede}</p>
        </div>
        <div className="regions-grid">
          {copy.regions.items.map(([name, area, logo]) => (
            <a className="region-card" href="#contact" key={name}>
              <div className="region-card__logo">
                <img src={logo} alt={name} loading="lazy" />
              </div>
              <span className="region-card__name">{name}<small>{area}</small></span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function Why({ copy }) {
  return (
    <section className="section section--blue" id="why" data-screen-label="07 Why CRMaps">
      <div className="section-inner">
        <div className="section-heading-left section-heading-left--white">
          <div className="kicker">{copy.why.kicker}</div>
          <h2 className="title">{copy.why.title} <em>{copy.why.em}</em></h2>
          <p className="lede">{copy.why.lede}</p>
        </div>
        <div className="why-grid">
          {copy.why.items.map((item, index) => <article className="why-card" key={item}><div className="why-card__num">{String(index + 1).padStart(2, "0")}</div><p>{item}</p></article>)}
        </div>
      </div>
    </section>
  );
}

function SalesContact({ copy }) {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const messageRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (messageRef.current) messageRef.current.value = e.detail.message;
    };
    window.addEventListener('crmaps-contact-prefill', handler);
    return () => window.removeEventListener('crmaps-contact-prefill', handler);
  }, []);
  const handleSubmit = async (event) => {
    event.preventDefault();
    setSent(false);
    setError("");
    setSending(true);
    const form = event.currentTarget;
    const payload = Object.fromEntries(new FormData(form).entries());
    payload.source = "for-business.html";
    try {
      const response = await fetch("/send-email.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.ok) {
        throw new Error(result.error || "Contact request failed");
      }
      form.reset();
      setSent(true);
    } catch (err) {
      setError(copy.contact.error);
    } finally {
      setSending(false);
    }
  };
  return (
    <section className="section" id="contact" data-screen-label="08 Contact">
      <div className="section-inner">
        <div className="contact-grid">
          <div className="contact-info">
            <span className="kicker">{copy.contact.kicker}</span>
            <h3>{copy.contact.title} <em style={{ color: "var(--crmaps-orange)", fontStyle: "normal" }}>{copy.contact.em}</em></h3>
            <p>{copy.contact.lede}</p>
            <div className="contact-channels">
              <a className="contact-channel" href="https://wa.me/50647073168">
                <img src="/imagenes/icons/whatsapp-icon.png" alt="" />
                <div><strong>{copy.contact.whatsapp}</strong><span>{copy.contact.whatsappSub}</span></div>
              </a>
              <a className="contact-channel" href="tel:+50626435084">
                <img src="/imagenes/icons/phone-icon.png" alt="" />
                <div><strong>{copy.contact.call}</strong><span>{copy.contact.callSub}</span></div>
              </a>
              <a className="contact-channel" href="mailto:info@crmaps.com">
                <img src="/imagenes/icons/web-icon.png" alt="" />
                <div><strong>{copy.contact.email}</strong><span>{copy.contact.emailSub}</span></div>
              </a>
            </div>
          </div>
          <form className="contact-form" onSubmit={handleSubmit}>
            {sent &&
              <div style={{ padding: 14, background: "rgba(0,136,62,0.1)", border: "1px solid rgba(0,136,62,0.3)", borderRadius: 10, color: "#166534", font: "600 14px Roboto", marginBottom: 18 }}>
                {copy.contact.success}
              </div>
            }
            {error &&
              <div style={{ padding: 14, background: "rgba(220,38,38,0.1)", border: "1px solid rgba(220,38,38,0.25)", borderRadius: 10, color: "#b91c1c", font: "600 14px Roboto", marginBottom: 18 }}>
                {error}
              </div>
            }
            <div className="form-row">
              <div className="form-field">
                <label>{copy.contact.labels.name}</label>
                <input required type="text" name="name" placeholder="María Hernández" />
              </div>
              <div className="form-field">
                <label>{copy.contact.labels.business}</label>
                <input required type="text" name="business" placeholder="El Avión Restaurante" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>{copy.contact.labels.email}</label>
                <input required type="email" name="email" placeholder="hola@example.com" />
              </div>
              <div className="form-field">
                <label>{copy.contact.labels.whatsapp}</label>
                <input type="tel" name="whatsapp" placeholder="+506 ..." />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>{copy.contact.labels.type}</label>
                <select name="type">
                  {copy.contact.businessTypes.map((option) => <option key={option}>{option}</option>)}
                </select>
              </div>
              <div className="form-field">
                <label>{copy.contact.labels.region}</label>
                <select name="region">
                  {copy.contact.regions.map((option) => <option key={option}>{option}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row form-row--full">
              <div className="form-field">
                <label>{copy.contact.labels.message}</label>
                <textarea ref={messageRef} name="message" placeholder={copy.contact.messagePlaceholder} />
              </div>
            </div>
            <div className="form-actions">
              <small>{copy.contact.disclaimer}</small>
              <button type="submit" className="btn btn--primary" disabled={sending}>{sending ? copy.contact.sending : copy.contact.submit}</button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function SalesFooter({ copy }) {
  const footer = copy.footer;
  const [openModal, setOpenModal] = useState(null);
  const modalContent = openModal === "about"
    ? { title: footer.aboutModalTitle, updated: null, paragraphs: footer.aboutModalText }
    : openModal === "privacy"
      ? { title: footer.privacyModalTitle, updated: footer.privacyUpdated, paragraphs: footer.privacyModalText }
      : null;

  useEffect(() => {
    if (!openModal) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpenModal(null);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openModal]);

  return (
    <footer className="site-footer" id="footer" style={{ backgroundColor: "rgb(0, 67, 130)" }}>
      <div className="sf-grid">
        <div className="sf-brand">
          <img src="/imagenes/index/logo_2026_white.webp" alt="CRMaps" />
          <p>{footer.brand}</p>
          <div className="sf-social">
            <a href={CONTACT.facebook} aria-label="Facebook">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
            </a>
            <a href={CONTACT.instagram} aria-label="Instagram">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none"/></svg>
            </a>
            <a href="https://wa.me/50647073168" aria-label="WhatsApp">
              <svg viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
            </a>
          </div>
        </div>
        <div className="sf-col">
          <h5>{footer.business}</h5>
          <ul>
            <li><a href="/for-business.html">{footer.listPin}</a></li>
            <li><a href="/for-business.html">{footer.sponsorRegion}</a></li>
            <li><a href="#contact">{footer.talkSales}</a></li>
          </ul>
        </div>
        <div className="sf-col">
          <h5>{footer.company}</h5>
          <ul>
            <li><button className="footer-link-button" type="button" onClick={() => setOpenModal("about")}>{footer.about}</button></li>
            <li><a href="/#community">{footer.community}</a></li>
            <li><a href="#contact">{footer.contact}</a></li>
            <li><button className="footer-link-button" type="button" onClick={() => setOpenModal("privacy")}>{footer.privacy}</button></li>
          </ul>
        </div>
      </div>
      <div className="sf-bottom"><span>{footer.bottom}</span></div>
      {modalContent && (
        <div className="footer-modal" role="dialog" aria-modal="true" aria-labelledby="footer-about-title">
          <button className="footer-modal__backdrop" type="button" aria-label={footer.close} onClick={() => setOpenModal(null)} />
          <div className="footer-modal__panel">
            <button className="footer-modal__close" type="button" aria-label={footer.close} onClick={() => setOpenModal(null)}>×</button>
            <h3 id="footer-about-title">{modalContent.title}</h3>
            {modalContent.updated && <p className="footer-modal__updated">{modalContent.updated}</p>}
            {modalContent.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
          </div>
        </div>
      )}
    </footer>
  );
}

Object.assign(window, {
  SalesNav,
  SalesHero,
  Ecosystem,
  Included,
  PrintedPresence,
  Pricing,
  Regions,
  Why,
  SalesContact,
  SalesFooter
});
